/*
 * Review carousel
 * Mobile shows one card at a time; desktop shows the configured number of cards
 * per row. Prev/next arrows are hidden until there are more cards than fit in the
 * current view, then the row scrolls one card at a time.
 */
class ReviewCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-rc-track]');
    this.slides = Array.from(this.querySelectorAll('.review-carousel__slide'));
    this.arrows = this.querySelector('[data-rc-arrows]');
    this.prevButton = this.querySelector('[data-rc-prev]');
    this.nextButton = this.querySelector('[data-rc-next]');

    if (!this.track || this.slides.length === 0) return;

    this.index = 0;
    this.desktopPerPage = parseInt(this.dataset.perPageDesktop, 10) || 3;
    this.desktopQuery = window.matchMedia('(min-width: 990px)');

    this.prevButton.addEventListener('click', () => this.move(-1));
    this.nextButton.addEventListener('click', () => this.move(1));

    this.setupSwipe();

    this.onResize = this.update.bind(this);
    window.addEventListener('resize', this.onResize);
    if (this.desktopQuery.addEventListener) {
      this.desktopQuery.addEventListener('change', this.onResize);
    }

    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    if (this.desktopQuery && this.desktopQuery.removeEventListener) {
      this.desktopQuery.removeEventListener('change', this.onResize);
    }
    this.track.removeEventListener('touchstart', this.onTouchStart);
    this.track.removeEventListener('touchmove', this.onTouchMove);
    this.track.removeEventListener('touchend', this.onTouchEnd);
    this.track.removeEventListener('touchcancel', this.onTouchEnd);
  }

  // Touch-drag the track so cards can be swiped through (primarily on mobile,
  // where one card shows at a time). The track follows the finger while
  // dragging, then snaps to the nearest card on release.
  setupSwipe() {
    this.swiping = false;
    this.dragDelta = 0;
    this.lockedAxis = null;

    this.onTouchStart = (event) => {
      // Nothing to swipe to if everything already fits in view.
      if (event.touches.length !== 1 || this.slides.length <= this.perPage) return;
      const touch = event.touches[0];
      this.startX = touch.clientX;
      this.startY = touch.clientY;
      this.swiping = true;
      this.lockedAxis = null;
      this.dragDelta = 0;
      this.metrics = this.measure();
      this.track.style.transition = 'none';
    };

    this.onTouchMove = (event) => {
      if (!this.swiping) return;
      const touch = event.touches[0];
      const dx = touch.clientX - this.startX;
      const dy = touch.clientY - this.startY;

      // Decide once whether this gesture is a horizontal swipe or a vertical
      // scroll, then stay locked to that axis for the rest of the gesture.
      if (this.lockedAxis === null) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        this.lockedAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (this.lockedAxis === 'y') return; // let the page scroll vertically

      this.dragDelta = dx;
      const base = this.index * this.metrics.step;
      this.track.style.transform = `translateX(-${base - dx}px)`;
    };

    this.onTouchEnd = () => {
      if (!this.swiping) return;
      this.swiping = false;
      this.track.style.transition = '';

      if (this.lockedAxis === 'x') {
        const threshold = Math.min(60, this.metrics.slideWidth * 0.2);
        if (this.dragDelta <= -threshold) this.move(1);
        else if (this.dragDelta >= threshold) this.move(-1);
        else this.render();
      } else {
        this.render();
      }
      this.dragDelta = 0;
    };

    // pan-y (set in CSS) lets the browser keep vertical scrolling while we
    // handle horizontal gestures, so passive listeners are fine here.
    this.track.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.track.addEventListener('touchmove', this.onTouchMove, { passive: true });
    this.track.addEventListener('touchend', this.onTouchEnd);
    this.track.addEventListener('touchcancel', this.onTouchEnd);
  }

  // Slide width plus the gap between slides — the distance one card occupies.
  measure() {
    const styles = window.getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    const slideWidth = this.slides[0].getBoundingClientRect().width;
    return { gap, slideWidth, step: slideWidth + gap };
  }

  get perPage() {
    return this.desktopQuery.matches ? this.desktopPerPage : 1;
  }

  get maxIndex() {
    return Math.max(0, this.slides.length - this.perPage);
  }

  move(direction) {
    this.index = Math.min(this.maxIndex, Math.max(0, this.index + direction));
    this.render();
  }

  update() {
    // Expose the per-row count to CSS so it can size each card.
    this.style.setProperty('--rc-per-page', this.perPage);

    const needsNavigation = this.slides.length > this.perPage;
    this.classList.toggle('review-carousel--static', !needsNavigation);
    this.arrows.hidden = !needsNavigation;

    this.index = Math.min(this.index, this.maxIndex);
    this.render();
  }

  render() {
    const offset = this.index * this.measure().step;

    this.track.style.transform = `translateX(-${offset}px)`;
    this.prevButton.disabled = this.index <= 0;
    this.nextButton.disabled = this.index >= this.maxIndex;
  }
}

customElements.define('review-carousel', ReviewCarousel);
